import { IntelligenceExcelCell as ApiIntelligenceExcelCell } from 'api/models/IntelligenceExcelCell';
import type { SummaryDataResponse } from 'api/models/SummaryDataResponse';
import type { SummaryRow } from 'api/models/SummaryRow';
import type { Format } from 'common/types/format';
import {
  COLUMN_TYPES,
  getValueType,
  isDraftSensitiveValueType,
  type SummaryDefinition,
} from 'common/types/summaryDefinitions';
import type { DisplayFormat } from 'common/types/summaryDefinitions/valueTypes/displayFormats';
import { FIRM_SUMMARY_DRAFT_TITLE } from 'pages/Firms/common/constants';
import { getCurrencyValue, shortDate } from 'utilities';

const COMPANY_NAME_COLUMN = 'company_name';
const DRAFT_STATUS = 'DRAFT';

export type IntelligenceExcelCell = Omit<ApiIntelligenceExcelCell, 'value'> & {
  value: string | number | boolean | null;
};

const FORMAT_TYPE = ApiIntelligenceExcelCell.format_type;

export type IntelligenceExcelExportPayload = {
  subtitle: string;
  columns: Array<{ header: string; is_numeric: boolean }>;
  rows: Array<{
    cells: IntelligenceExcelCell[];
    is_emphasis: boolean;
    is_child: boolean;
  }>;
};

const orderedColumnTypes = (definition: SummaryDefinition): string[] => {
  const columns = definition.spreadsheet_definition?.columns ?? definition.spreadsheetDefinition?.columns;
  if (!columns) {
    return [COMPANY_NAME_COLUMN];
  }

  const ordered = Object.entries(columns)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([, columnType]) => columnType);

  if (!ordered.includes(COMPANY_NAME_COLUMN)) {
    return [COMPANY_NAME_COLUMN, ...ordered];
  }
  return ordered;
};

const columnHeader = (columnType: string): string =>
  getValueType(columnType)?.displayName ??
  columnType.replace(/_/g, ' ').replace(/\b\w/g, character => character.toUpperCase());

const displayFormatFor = (columnType: string): DisplayFormat | undefined => getValueType(columnType)?.displayFormat;

const applyDisplayScale = (value: number, format: Format | null | undefined): number => {
  const exchangeRate = format?.currency?.exchangeRate ?? 1;
  const units = format?.currency?.units;
  return getCurrencyValue({ value, exchangeRate, units });
};

const formatCell = (value: unknown, columnType: string, format: Format | null | undefined): IntelligenceExcelCell => {
  const displayFormat = displayFormatFor(columnType);
  const formatType = displayFormat?.type;

  if (value === null || value === undefined || value === '') {
    const formatTypes = {
      boolean: FORMAT_TYPE.BOOLEAN,
      currency: FORMAT_TYPE.CURRENCY,
      date: FORMAT_TYPE.DATE,
      number: FORMAT_TYPE.NUMBER,
      percentage: FORMAT_TYPE.PERCENTAGE,
      text: FORMAT_TYPE.TEXT,
    };
    return {
      value: null,
      format_type: formatType && formatType !== 'custom' ? formatTypes[formatType] : FORMAT_TYPE.TEXT,
    };
  }

  if (Array.isArray(value)) {
    return {
      value: value.length ? value.map(entry => String(entry)).join(', ') : null,
      format_type: FORMAT_TYPE.TEXT,
    };
  }

  if (!displayFormat) {
    return { value: String(value), format_type: FORMAT_TYPE.TEXT };
  }

  switch (displayFormat.type) {
    case 'currency': {
      const numeric = Number(value);
      return {
        value: Number.isFinite(numeric) ? applyDisplayScale(numeric, format) : null,
        format_type: FORMAT_TYPE.CURRENCY,
        decimals: 0,
        currency_symbol: format?.currency?.symbol ?? '$',
      };
    }
    case 'number': {
      const numeric = Number(value);
      return {
        value: Number.isFinite(numeric) ? numeric : null,
        format_type: FORMAT_TYPE.NUMBER,
        decimals: displayFormat.decimals ?? 2,
        thousands_separator: displayFormat.thousandsSeparator ?? false,
        suffix: displayFormat.thousandsSeparator === false ? 'x' : undefined,
      };
    }
    case 'percentage': {
      const numeric = Number(value);
      return {
        value: Number.isFinite(numeric) && displayFormat.multiplier === 1 ? numeric / 100 : numeric,
        format_type: FORMAT_TYPE.PERCENTAGE,
        decimals: displayFormat.decimals ?? 2,
      };
    }
    case 'date':
      return {
        value: shortDate(String(value)) ?? String(value),
        format_type: FORMAT_TYPE.DATE,
      };
    case 'boolean':
      return {
        value: value ? (displayFormat.trueLabel ?? 'Yes') : (displayFormat.falseLabel ?? 'No'),
        format_type: FORMAT_TYPE.BOOLEAN,
      };
    default:
      return { value: String(value), format_type: FORMAT_TYPE.TEXT };
  }
};

// Mirrors the sheet's draft treatment (normalizeDefinitionRows + createColumns): on a draft row,
// Total Value reads DRAFT and every other draft-sensitive column is blank.
const draftCell = (columnType: string): IntelligenceExcelCell => ({
  value: columnType === COLUMN_TYPES.TOTAL_VALUE ? FIRM_SUMMARY_DRAFT_TITLE : null,
  format_type: FORMAT_TYPE.TEXT,
});

const isDraftSensitiveColumn = (columnType: string): boolean => {
  const valueType = getValueType(columnType);
  return valueType ? isDraftSensitiveValueType(valueType) : false;
};

const rowValue = (row: SummaryRow, columnType: string): unknown => {
  const record = row as SummaryRow & Record<string, unknown>;
  if (columnType === 'valuation_date') {
    return record.measurement_date ?? record.valuation_date;
  }
  return record[columnType];
};

export const buildSpreadsheetExportPayload = (
  definition: SummaryDefinition,
  data: SummaryDataResponse,
  format: Format | null | undefined,
  subtitle: string
): IntelligenceExcelExportPayload => {
  const columns = orderedColumnTypes(definition);
  const rollupCompanies = new Set(
    (data.rows ?? []).filter(row => row.row_type === 'company_rollup').map(row => row.company_name)
  );
  // Older snapshots' rollup rows carry no valuation_status: a draft leaf row drafts its rollup too
  const draftCompanies = new Set(
    (data.rows ?? [])
      .filter(row => row.row_type !== 'company_rollup' && row.valuation_status === DRAFT_STATUS)
      .map(row => row.company_name)
  );
  const isDraftRow = (row: SummaryRow): boolean =>
    row.valuation_status === DRAFT_STATUS ||
    (row.row_type === 'company_rollup' && draftCompanies.has(row.company_name));

  return {
    subtitle,
    columns: columns.map(columnType => ({
      header: columnHeader(columnType),
      is_numeric: ['currency', 'percentage', 'number'].includes(displayFormatFor(columnType)?.type ?? ''),
    })),
    rows: (data.rows ?? []).map(row => ({
      cells: columns.map(columnType =>
        isDraftRow(row) && isDraftSensitiveColumn(columnType)
          ? draftCell(columnType)
          : formatCell(rowValue(row, columnType), columnType, format)
      ),
      is_emphasis: row.row_type === 'company_rollup',
      is_child: row.row_type === 'company' && rollupCompanies.has(row.company_name),
    })),
  };
};
